import { useEffect, useState, SyntheticEvent } from 'react'
import {
    Box,
    Grid,
    Card,
    CardContent,
    CardActions,
    Typography,
    Divider,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Button,
    TextField,
    Autocomplete,
    Checkbox,
    FormControlLabel,
    Tooltip,
    IconButton,
    CardHeader
} from '@mui/material'
import Icon from '../../../components/icon'
import axios from 'axios'
import moment from 'moment'
import 'moment/locale/id'
import Swal from 'sweetalert2'
import apiUrl from '../../../configs/constant'

type StepType = 'rekalkulasi' | 'posting'

interface StepItem {
    type: StepType
    label: string
    step?: number
    transaksi?: string
    sp?: string
    status: boolean | null | undefined
    message: string
}

const Index = () => {
    const userData = JSON.parse(localStorage.getItem('user')!)
    const token = userData.token

    const [mode, setMode] = useState({
        rekalkulasi: true,
        posting: true
    })

    const [listCombo, setListCombo] = useState<any[]>([])
    const [listDetail, setListDetail] = useState<any[]>([])
    const [listData, setListData] = useState<any[]>([])

    const [listPeriodeAwal, setListPeriodeAwal] = useState<any[]>([])
    const [listPeriodeAkhir, setListPeriodeAkhir] = useState<any[]>([])
    const [valPeriodeAwal, setValPeriodeAwal] = useState<any>(null)
    const [valPeriodeAkhir, setValPeriodeAkhir] = useState<any>(null)

    const [cardStates, setCardStates] = useState<Record<number, { loading: boolean; arrItems: StepItem[] }>>({})

    // ================= INIT =================
    useEffect(() => {
        getListPeriode()
        getListCombo()
        getDetailSetting()
    }, [])

    useEffect(() => {
        if (!listData.length) return

        const init: any = {}

        listData.forEach(item => {
            init[item.id] = {
                loading: false,
                arrItems: buildSteps()
            }
        })

        setCardStates(init)
    }, [listData, mode, listCombo, listDetail])

    // ================= BUILD STEP =================
    const buildSteps = (): StepItem[] => {
        const steps: StepItem[] = []

        if (mode.rekalkulasi) {
            listCombo.forEach((s, i) => {
                steps.push({
                    type: 'rekalkulasi',
                    label: s.label,
                    step: i + 1,
                    status: null,
                    message: ''
                })
            })
        }

        if (mode.posting) {
            listDetail.forEach((s: any) => {
                steps.push({
                    type: 'posting',
                    label: s.transaksi,
                    transaksi: s.transaksi,
                    sp: s.sp,
                    status: null,
                    message: ''
                })
            })
        }

        return steps
    }

    // ================= API =================
    const getListPeriode = async () => {
        const res = await axios.get(`${apiUrl.apiUrl}/acc/laporan-keuangan/list-periode`, {
            headers: { Authorization: `Bearer ${token}` }
        })

        const data = res.data.data
        setListPeriodeAwal(data)
        setListPeriodeAkhir(data)

        setValPeriodeAwal(data[0])
        setValPeriodeAkhir(data[0])

        getDataFilter(data[0].id, data[0].id)
    }

    const getListCombo = async () => {
        const res = await axios.get(`${apiUrl.apiUrl}/acc/laporan-keuangan/list-combo-rekalkulasi`, {
            headers: { Authorization: `Bearer ${token}` }
        })
        setListCombo(res.data.data)
    }

    const getDetailSetting = async () => {
        const res = await axios.get(`${apiUrl.apiUrl}/acc/posting-jurnal/detail-setting`, {
            headers: { Authorization: `Bearer ${token}` }
        })
        setListDetail(res.data.data)
    }

    const getDataFilter = async (awal: any, akhir: any) => {
        Swal.fire({ title: 'Loading...', didOpen: () => Swal.showLoading() })

        const res = await axios.get(`${apiUrl.apiUrl}/acc/laporan-keuangan/master-rekalkulasi-persediaan`, {
            params: { tgl_awal: awal, tgl_akhir: akhir },
            headers: { Authorization: `Bearer ${token}` }
        })

        setListData(res.data.data)
        Swal.close()
    }

    // ================= EXECUTE STEP =================
    const runStep = async (item: any, step: StepItem, stepIndex: number) => {
        setCardStates(prev => {
            const arr = [...prev[item.id].arrItems]
            arr[stepIndex] = {
                ...arr[stepIndex],
                status: undefined, // <-- ini penting (loading state)
                message: 'Memproses...'
            }
            return { ...prev, [item.id]: { ...prev[item.id], arrItems: arr } }
        })

        try {
            let res

            if (step.type === 'rekalkulasi') {
                res = await axios.post(
                    `${apiUrl.apiUrl}/acc/laporan-keuangan/detail-rekalkulasi-persediaan`,
                    {
                        step: step.step,
                        id_periode: item.id
                    },
                    { headers: { Authorization: `Bearer ${token}` } }
                )
            } else {
                res = await axios.post(
                    `${apiUrl.apiUrl}/acc/posting-jurnal/posting`,
                    {
                        transaksi: step.transaksi,
                        sp: step.sp,
                        tgl_awal: moment(item.tgl_awal).format('YYYY-MM-DD'),
                        tgl_akhir: moment(item.tgl_akhir).format('YYYY-MM-DD')
                    },
                    { headers: { Authorization: `Bearer ${token}` } }
                )
            }

            const success = !!res.data.success

            setCardStates(prev => {
                const arr = [...prev[item.id].arrItems]
                arr[stepIndex] = { ...arr[stepIndex], status: success, message: res.data.message }
                return { ...prev, [item.id]: { ...prev[item.id], arrItems: arr } }
            })

            return success
        } catch (err: any) {
            setCardStates(prev => {
                const arr = [...prev[item.id].arrItems]
                arr[stepIndex] = {
                    ...arr[stepIndex],
                    status: false,
                    message: err.message
                }
                return { ...prev, [item.id]: { ...prev[item.id], arrItems: arr } }
            })
            return false
        }
    }

    // ================= BULK =================
    const handleBulk = async (item: any) => {
        const steps = cardStates[item.id].arrItems

        setCardStates(prev => ({
            ...prev,
            [item.id]: { ...prev[item.id], loading: true }
        }))

        for (let i = 0; i < steps.length; i++) {

            // SET LOADING STEP SAAT INI
            setCardStates(prev => {
                const arr = [...prev[item.id].arrItems]
                arr[i] = { ...arr[i], status: undefined, message: 'Memproses...' }
                return { ...prev, [item.id]: { ...prev[item.id], arrItems: arr } }
            })

            const ok = await runStep(item, steps[i], i)

            if (!ok) break // stop kalau gagal

            await new Promise(r => setTimeout(r, 200)) // biar smooth
        }

        setCardStates(prev => ({
            ...prev,
            [item.id]: { ...prev[item.id], loading: false }
        }))
    }


    // ================= UI =================
    return (
        <Grid container spacing={3}>
            <Grid item xs={12}>
                <Card>
                    <Box
                        sx={{
                            width: '100%',
                            display: 'flex',
                            flexWrap: 'wrap',
                            alignItems: 'center',
                            justifyContent: 'space-between'
                        }}
                    >
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center' }}>
                            <CardHeader title='POSTING' />
                        </Box>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center' }}></Box>
                    </Box>
                    <Divider />
                    <CardContent>

                        {/* FILTER */}
                        <Grid container spacing={2}>
                            <Grid item xs={3}>
                                <Autocomplete
                                    value={valPeriodeAwal}
                                    options={listPeriodeAwal}
                                    getOptionLabel={o => o.periode}
                                    onChange={(e, v) => setValPeriodeAwal(v)}
                                    renderInput={p => <TextField {...p} label="Periode Awal" />}
                                />
                            </Grid>

                            <Grid item xs={3}>
                                <Autocomplete
                                    value={valPeriodeAkhir}
                                    options={listPeriodeAkhir}
                                    getOptionLabel={o => o.periode}
                                    onChange={(e, v) => setValPeriodeAkhir(v)}
                                    renderInput={p => <TextField {...p} label="Periode Akhir" />}
                                />
                            </Grid>

                            <Grid item xs={3}>
                                <Button
                                    variant="contained"
                                    onClick={() => getDataFilter(valPeriodeAwal.id, valPeriodeAkhir.id)}
                                >
                                    Cari
                                </Button>
                            </Grid>
                        </Grid>

                        {/* CHECKBOX */}
                        <Box sx={{ mt: 2 }}>
                            <FormControlLabel
                                control={<Checkbox checked={mode.rekalkulasi} onChange={() => setMode({ ...mode, rekalkulasi: !mode.rekalkulasi })} />}
                                label="Rekalkulasi"
                            />
                            <FormControlLabel
                                control={<Checkbox checked={mode.posting} onChange={() => setMode({ ...mode, posting: !mode.posting })} />}
                                label="Posting Jurnal"
                            />
                        </Box>

                        {/* CARD */}
                        <Grid container spacing={3} sx={{ mt: 2 }}>
                            {listData.map(item => {
                                const card = cardStates[item.id]
                                if (!card) return null

                                return (
                                    <Grid item xs={4} key={item.id}>
                                        <Card>
                                            <CardContent>

                                                <Typography>{item.keterangan}</Typography>

                                                <Divider sx={{ my: 2 }} />

                                                <List>
                                                    {card.arrItems.map((step, i) => (
                                                        <ListItem
                                                            key={i}
                                                            // secondaryAction={
                                                            //     <IconButton onClick={() => runStep(item, step, i)}>
                                                            //         <Icon icon="mdi:play-circle" />
                                                            //     </IconButton>
                                                            // }
                                                            secondaryAction={
                                                                <ListItemIcon sx={{ minWidth: 32 }}>
                                                                    {(() => {
                                                                        if (step.status === true) {
                                                                            return (
                                                                                <IconButton>
                                                                                    <Icon icon="mdi:check-circle" color="green" fontSize={22} />
                                                                                </IconButton>
                                                                            )
                                                                        } else if (step.status === false) {
                                                                            return (
                                                                                <Tooltip title={step.message || ''}>
                                                                                    <IconButton onClick={() => runStep(item, step, i)}>
                                                                                        <Icon icon="mdi:close-circle" color="red" fontSize={22} />
                                                                                    </IconButton>
                                                                                </Tooltip>
                                                                            )
                                                                        } else if (step.status === null) {
                                                                            return (
                                                                                <IconButton onClick={() => runStep(item, step, i)}>
                                                                                    <Icon icon="mdi:play-circle-outline" color="#999" fontSize={22} />
                                                                                </IconButton>
                                                                            )
                                                                        } else {
                                                                            // loading (undefined)
                                                                            return (
                                                                                <Icon icon="mdi:loading" className="spin" fontSize={22} />
                                                                            )
                                                                        }
                                                                    })()}
                                                                </ListItemIcon>
                                                            }
                                                        >
                                                            <ListItemText primary={step.label} />
                                                        </ListItem>
                                                    ))}
                                                </List>
                                            </CardContent>

                                            <CardActions>
                                                <Button
                                                    fullWidth
                                                    variant="contained"
                                                    disabled={card.loading}
                                                    onClick={() => handleBulk(item)}
                                                >
                                                    {card.loading ? 'PROSES...' : 'JALANKAN SEMUA'}
                                                </Button>
                                            </CardActions>
                                        </Card>
                                    </Grid>
                                )
                            })}
                        </Grid>

                    </CardContent>
                </Card>
            </Grid>
        </Grid>
    )
}

export default Index
